/**
 * @file apps/web/src/lib/projectOverviewEvents.ts
 * @description Tiny in-app signal for "something that feeds a project's phase
 * gates just changed". The sidebar (AppShell's ProjectNav) fetches
 * GET /projects/:id/overview once and used to never look again, so finishing
 * the last Discovery node left Design locked until a page refresh. Pages that
 * change gate inputs call notifyProjectOverviewChanged(projectId) after a
 * successful write; ProjectNav subscribes and refetches.
 *
 * Deliberately a plain module-level registry (no React, no window events, no
 * context) so it is trivially testable and works across any component tree.
 * Same-tab only — a change made by another user or tab is picked up by
 * ProjectNav's refetch-on-focus instead (Supabase Realtime is not wired up).
 */

type Listener = () => void;

const listeners = new Map<string, Set<Listener>>();

/** Subscribes to gate-input changes for one project. Returns an unsubscribe fn. */
export function subscribeProjectOverviewChanged(projectId: string, listener: Listener): () => void {
  let set = listeners.get(projectId);
  if (!set) {
    set = new Set();
    listeners.set(projectId, set);
  }
  set.add(listener);
  return () => {
    const current = listeners.get(projectId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(projectId);
  };
}

/** Tells every subscriber for this project to refetch its overview data. */
export function notifyProjectOverviewChanged(projectId: string): void {
  const set = listeners.get(projectId);
  if (!set) return;
  // Copy first: a listener may unsubscribe (or subscribe) while we iterate.
  for (const listener of [...set]) {
    try {
      listener();
    } catch (err) {
      // One broken subscriber must never stop the others from refreshing.
      console.error('[projectOverviewEvents] listener threw:', err);
    }
  }
}

/**
 * Wraps an async loader so only the most recently STARTED call may apply its
 * result. Two quick saves fire two refetches; if the older response lands
 * second it would otherwise overwrite the newer, correct state (e.g.
 * re-locking Design right after it unlocked).
 */
export function createLatestOnly<T>(
  load: () => Promise<T>,
  apply: (value: T) => void,
  onError?: (err: unknown) => void,
): () => Promise<void> {
  let latest = 0;
  return async () => {
    const mine = ++latest;
    try {
      const value = await load();
      if (mine === latest) apply(value);
    } catch (err) {
      if (mine === latest) onError?.(err);
    }
  };
}
